import React, { useEffect, useMemo } from "react";
import {
  Button,
  Table,
  Tag,
  Space,
  Select,
  Input,
  Badge,
  Popconfirm,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import {
  IKlearlyAdmin,
  AdminRole,
  EmployeeType,
} from "../../../../interfaces/klearlyAdmin.interface";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  selectIsKlearlySuperAdmin,
  selectUser,
} from "../../../access-control/accountSlice";
import {
  deleteAdmin,
  fetchAdmins,
  selectAdmins,
  selectAdminsFilter,
  selectAdminsLoading,
  setEmployeeTypeFilter,
  setSearchFilter,
} from "../../adminsSlice";
import styles from "./AdminsTable.module.css";

interface AdminsTableProps {
  onEdit: (admin: IKlearlyAdmin) => void;
  onCreate: () => void;
}

export default function AdminsTable({ onEdit, onCreate }: AdminsTableProps) {
  const dispatch = useAppDispatch();
  const admins = useAppSelector(selectAdmins);
  const loading = useAppSelector(selectAdminsLoading);
  const { employeeType, search } = useAppSelector(selectAdminsFilter) || {};
  const isSuperAdmin = useAppSelector(selectIsKlearlySuperAdmin);
  const currentUser = useAppSelector(selectUser);

  useEffect(() => {
    dispatch(
      fetchAdmins(employeeType || search ? { employeeType, search } : undefined)
    );
  }, [dispatch, employeeType, search]);

  const roleLabel: Record<AdminRole, string> = useMemo(
    () => ({
      SuperAdmin: "Super Admin",
      Admin: "Admin",
      Sales: "Sales",
      SuperSalesAdmin: "Sales Super Admin",
      TerminalCoupling: "Terminal Coupling",
      Analytics: "Analytics",
    }),
    []
  );

  const roleColor: Record<AdminRole, string> = useMemo(
    () => ({
      SuperAdmin: "red",
      Admin: "blue",
      Sales: "green",
      SuperSalesAdmin: "gold",
      TerminalCoupling: "purple",
      Analytics: "geekblue",
    }),
    []
  );

  const columns: ColumnsType<IKlearlyAdmin> = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Employee Type",
        dataIndex: "employeeType",
        key: "employeeType",
        render: (value: EmployeeType) => {
          const label = value
            ? value.charAt(0).toUpperCase() + value.slice(1)
            : "";
          const statusMap: Record<
            EmployeeType,
            "success" | "processing" | "default" | "error" | "warning"
          > = {
            sales: "processing",
            operations: "warning",
            support: "default",
            general: "default",
            coreTech: "success",
          };
          return <Badge status={statusMap[value]} text={label} />;
        },
        filters: [
          { text: "sales", value: "sales" },
          { text: "operations", value: "operations" },
          { text: "support", value: "support" },
          { text: "general", value: "general" },
          { text: "coreTech", value: "coreTech" },
        ],
        onFilter: (value, record) => record.employeeType === value,
      },
      {
        title: "Roles",
        dataIndex: "roles",
        key: "roles",
        render: (roles: AdminRole[]) => (
          <Space wrap>
            {roles?.map((r) => (
              <Tag key={r} color={roleColor[r]}>
                {roleLabel[r] || r}
              </Tag>
            ))}
          </Space>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, row) => {
          const isSelf =
            !!currentUser?.email &&
            row.email?.toLowerCase() === currentUser.email.toLowerCase();
          return (
            <div className={styles.row_actions}>
              <Button size="small" onClick={() => onEdit(row)}>
                Edit
              </Button>
              {isSuperAdmin && !isSelf ? (
                <Popconfirm
                  title="Delete admin?"
                  description={`This removes ${row.email} from Cognito and Mongo. This cannot be undone.`}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                  icon={
                    <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
                  }
                  onConfirm={() => dispatch(deleteAdmin(row.email))}
                >
                  <Button size="small" danger>
                    Delete
                  </Button>
                </Popconfirm>
              ) : null}
            </div>
          );
        },
      },
    ],
    [onEdit, roleLabel, roleColor, isSuperAdmin, currentUser?.email, dispatch]
  );

  return (
    <div>
      <div
        style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}
      >
        <Input.Search
          placeholder="Search by name or email"
          allowClear
          style={{ width: 260 }}
          defaultValue={search}
          onSearch={(value) =>
            dispatch(
              setEmployeeTypeFilter(employeeType as EmployeeType | undefined)
            ) || dispatch(setSearchFilter(value || undefined))
          }
          onChange={(e) =>
            dispatch(setSearchFilter(e.target.value || undefined))
          }
        />
        <Select
          allowClear
          placeholder="Filter by employee type"
          value={employeeType}
          style={{ width: 220 }}
          onChange={(val) =>
            dispatch(setEmployeeTypeFilter(val as EmployeeType | undefined))
          }
          options={[
            { label: "sales", value: "sales" },
            { label: "operations", value: "operations" },
            { label: "support", value: "support" },
            { label: "general", value: "general" },
            { label: "coreTech", value: "coreTech" },
          ]}
        />
      </div>
      <Table
        size="middle"
        bordered
        rowKey={(r) => r.email || r.id}
        dataSource={admins}
        columns={columns}
        loading={loading}
      />
    </div>
  );
}
